import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Dropdown, Badge, Alert, FloatingLabel } from "react-bootstrap";
import axios from "axios";
import moment from "moment";
import { API_ENDPOINTS } from '../../config/api';
import ProgramPGYUtils from '../../utils/programPGYUtils';
import { useInstitutionalData } from '../../contexts/InstitutionalDataContext';
import ExpandableUserSelector from '../ExpandableUserSelector';

const eventTypeColors = {
  'Clinic': '#aec6e4', // Blue
  'OR': '#ffb3ba',     // Red
  'Meeting': '#b2e0b2', // Green
  'Education': '#ffdfba', // Yellow
  'Journal Club': '#dbffba', // lime 
  'Personal': '#c3b1e1', // Purple
  'Vacation': '#c7eff7', // Blue
  'Sick': '#e9f4b1', // Yellow
  'Weekend Call': '#fab6a3', // Red
  'Other': '#b2e7e0', // Gray
  'default': '#aec6e4'  // Default blue
};

const AddEventModal = ({ show, handleClose, onEventAdded, selectedSlot }) => {
  const [formData, setFormData] = useState({
    title: "",
    start: selectedSlot ? moment.utc(selectedSlot.start).local().format("YYYY-MM-DDTHH:mm") : moment().format("YYYY-MM-DDTHH:mm"),
    end: selectedSlot ? moment.utc(selectedSlot.end).local().format("YYYY-MM-DDTHH:mm") : moment().add(1, 'hour').format("YYYY-MM-DDTHH:mm"),
    allDay: selectedSlot ? selectedSlot.allDay || false : false,
    repeat: "none",
    repeatEnd: "",
    location: "",
    label: "",
    description: "",
  });

  const [attachments, setAttachments] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [programMaxPGYById, setProgramMaxPGYById] = useState({});
  // Institutional admins can invite users from any program in their institution.
  const isInstitutionalAdmin = typeof window !== 'undefined'
    && localStorage.getItem('role') === 'institutional_admin';
  const institutionalCache = useInstitutionalData();
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // PGY level configuration
  const [pgyLevelOptions, setPgyLevelOptions] = useState([]);
  const [maxPGYLevel, setMaxPGYLevel] = useState(5);
  const [programDetails, setProgramDetails] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);

  // Add useEffect to update form data when selectedSlot changes
  useEffect(() => {
    if (selectedSlot) {
      setFormData(prev => ({
        ...prev,
              start: selectedSlot.formattedStart || moment.utc(selectedSlot.start).local().format("YYYY-MM-DDTHH:mm"),
      end: selectedSlot.formattedEnd || moment.utc(selectedSlot.end).local().format("YYYY-MM-DDTHH:mm"),
        allDay: selectedSlot.allDay || false
      }));
    }
  }, [selectedSlot]);

  // Fetch program details and PGY levels
  const fetchProgramDetails = async () => {
    setProgramLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${API_ENDPOINTS.PROGRAMS}/my-program-details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Program details received:', data);
        setProgramDetails(data);
        
        // Fetch PGY levels for this program
        if (data.id) {
          console.log('Fetching PGY levels for programId:', data.id);
          await fetchPGYLevels(data.id);
        } else {
          console.log('No programId found in program details');
        }
      }
    } catch (err) {
      console.error("Failed to fetch program details", err);
    } finally {
      setProgramLoading(false);
    }
  };

  // Fetch PGY level options for the program
  const fetchPGYLevels = async (programId) => {
    try {
      console.log('Fetching PGY levels for program:', programId);
      const options = await ProgramPGYUtils.getPGYLevelOptions(programId);
      console.log('Received PGY options:', options);
      setPgyLevelOptions(options);
      setMaxPGYLevel(options.length > 0 ? options[options.length - 1].value : 5);
    } catch (error) {
      console.error('Error fetching PGY levels:', error);
      // Fallback to default 5 levels
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`
      }));
      console.log('Using fallback PGY options:', fallbackOptions);
      setPgyLevelOptions(fallbackOptions);
      setMaxPGYLevel(5);
    }
  };

  // Fetch available users for invitation.
  // Institutional admins pull from /institutional-admin/users which returns
  // every user across every program in the institution (plus program info).
  // Everyone else uses the program-scoped /users endpoint as before.
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token");
        if (isInstitutionalAdmin) {
          // Use the shared cache so we don't re-fetch the institution-wide
          // user list on every modal open.
          const [users, programs] = await Promise.all([
            institutionalCache.fetchResource('users'),
            institutionalCache.fetchResource('programs'),
          ]);
          const list = Array.isArray(users) ? users : [];
          setAvailableUsers(list);
          const programList = Array.isArray(programs) ? programs : [];
          if (programList.length > 0) {
            setProgramOptions(programList.map((p) => ({ id: p.id, name: p.name })));
          } else {
            // Fallback: derive program list from the users' embedded program data.
            const byId = new Map();
            for (const u of list) {
              if (u.program && u.program.id && !byId.has(u.program.id)) {
                byId.set(u.program.id, { id: u.program.id, name: u.program.name });
              }
            }
            setProgramOptions(Array.from(byId.values()));
          }
        } else {
          const response = await axios.get(`${API_ENDPOINTS.USERS}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAvailableUsers(response.data);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    if (show) {
      fetchUsers();
      fetchProgramDetails(); // Fetch program details and PGY levels
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, isInstitutionalAdmin]);

  useEffect(() => {
    let cancelled = false;
    if (!isInstitutionalAdmin || !programOptions?.length) {
      setProgramMaxPGYById({});
      return;
    }
    (async () => {
      const pairs = await Promise.all(
        programOptions.map(async (p) => {
          const mx = await ProgramPGYUtils.getMaxPGYLevel(p.id);
          const n = typeof mx === "number" && Number.isFinite(mx) && mx > 0 ? Math.floor(mx) : 5;
          return [p.id, n];
        })
      );
      if (!cancelled) {
        const next = {};
        pairs.forEach(([id, mx]) => {
          next[id] = mx;
        });
        setProgramMaxPGYById(next);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInstitutionalAdmin, programOptions]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // If allDay is checked, set start to beginning of day and end to 11:59 PM of end day
      if (name === "allDay" && checked) {
        newData.start = moment(newData.start).format("YYYY-MM-DD");
        newData.end = moment(newData.end).format("YYYY-MM-DD");
      }

      return newData;
    });
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setAttachments(prev => [...prev, ...files]);
    setSuccess("");
    setError("");
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const eventData = {
        ...formData,
        programId: programDetails?.id || null,
        start: formData.allDay 
          ? moment(formData.start).startOf('day').toISOString()
          : new Date(formData.start).toISOString(),
        end: formData.allDay
          ? moment(formData.end).startOf('day').add(23, 'hours').add(59, 'minutes').toISOString()
          : new Date(formData.end).toISOString(),
        repeatEnd: formData.repeat !== "none" ? new Date(formData.repeatEnd).toISOString() : null,
        allDay: formData.allDay || false,
      };
      
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Please log in to create events");
        return;
      }

      // Create the event first
      const eventResponse = await axios.post(`${API_ENDPOINTS.EVENTS}/add`, eventData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const createdEvent = eventResponse.data;
      const eventId = Array.isArray(createdEvent) ? createdEvent[0].id : createdEvent.id;

      // Always create a creator invitation for the event creator
      console.log("Creating creator invitation for event:", eventId);
      await axios.post(`${API_ENDPOINTS.EVENT_INVITATIONS}/send/${eventId}`, {
        invitedUserIds: [] // Empty array means only creator invitation will be created
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Upload attachments if any
      if (attachments.length > 0) {
        for (const file of attachments) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('file', file);

          await axios.post(`${API_ENDPOINTS.EVENT_ATTACHMENTS}/upload/${eventId}`, attachmentFormData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        }
      }

      // Send additional invitations if any users are selected
      if (selectedUsers.length > 0) {
        console.log("Sending additional invitations for users:", selectedUsers);
        await axios.post(`${API_ENDPOINTS.EVENT_INVITATIONS}/send/${eventId}`, {
          invitedUserIds: selectedUsers
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      onEventAdded(createdEvent);
      handleClose();
      setSuccess("Event created successfully!");
    } catch (err) {
      console.error("Event creation failed", err);
      setError(err.response?.data?.error || "Failed to create event. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton className="px-4 border-0 mt-2">
        <div style={{width: '50%'}}>
        <Modal.Title>Add New Event</Modal.Title>
        <div className="text-hl"></div>
        </div>
      </Modal.Header>
      <Modal.Body className="px-4">

        {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-lg-4 col-md-8">
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Event Title" className="mb-3">
                <Form.Control 
                  name="title" 
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="Enter event title"
                />
                </FloatingLabel>
              </Form.Group>

              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Start Time" className="mb-3">
                <Form.Control 
                  type={formData.allDay ? "date" : "datetime-local"} 
                  name="start" 
                  value={formData.start}
                  onChange={handleChange}
                  required
                />
                </FloatingLabel>
              </Form.Group>

              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="End Time" className="mb-3">
                <Form.Control 
                  type={formData.allDay ? "date" : "datetime-local"} 
                  name="end" 
                  value={formData.end}
                  onChange={handleChange}
                  required
                />
                </FloatingLabel>
              </Form.Group>

              <Form.Check
                type="checkbox"
                label="All Day Event"
                name="allDay"
                checked={formData.allDay}
                onChange={handleChange}
                className="mb-3"
              />
            </div>

            <div className="col-lg-3 col-md-4" >
              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingInput" label="Location" className="mb-3">
                <Form.Control 
                  name="location" 
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Enter location"
                />
                </FloatingLabel>
              </Form.Group>

              <Form.Group className="mb-3">
                <FloatingLabel controlId="floatingEventType" label="Event Type" className="mb-3">
                  <Dropdown className="w-100">
                    <Dropdown.Toggle
                      variant="light"
                      id="floatingEventType"
                      className="form-select text-start w-100 no-arrow d-flex align-items-center gap-2"
                    style={{ minHeight: '58px'}}
                    >
                      {formData.label ? (
                        <>
                          <span
                            style={{
                              display: 'inline-block',
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: eventTypeColors[formData.label] || eventTypeColors.default,
                              flexShrink: 0,
                            }}
                          />
                          <span className="text-truncate">{formData.label}</span>
                        </>
                      ) : (
                        <span className="text-muted text-truncate">Select event type</span>
                      )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="w-100 no-arrow">
                      {Object.keys(eventTypeColors)
                        .filter((type) => type !== 'default')
                        .map((type) => (
                          <Dropdown.Item
                            key={type}
                            onClick={() => handleChange({ target: { name: 'label', value: type } })}
                            className="d-flex align-items-center gap-2"
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: eventTypeColors[type],
                                flexShrink: 0,
                              }}
                            />
                            {type}
                          </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </FloatingLabel>
              </Form.Group>

              <Form.Group className="">
                <FloatingLabel controlId="floatingInput" label="Repeat" className="mb-3">
                <Form.Select 
                  name="repeat" 
                  value={formData.repeat}
                  onChange={handleChange}
                >
                  <option value="none">No Repeat</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </Form.Select>
                </FloatingLabel>
              </Form.Group>

              {formData.repeat !== "none" && (
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="Repeat Until" className="mb-3">
                  <Form.Control 
                    type="date"
                    name="repeatEnd"
                    value={formData.repeatEnd}
                    onChange={handleChange}
                    required
                  />
                  </FloatingLabel>
                </Form.Group>
              )}
            </div>
            <div className="col-lg-5 col-md-12 bg-light p-2 rounded-2">
              {/* User Invitations Section */}
              <ExpandableUserSelector
                availableUsers={availableUsers}
                selectedUsers={selectedUsers}
                onSelectionChange={setSelectedUsers}
                pgyLevelOptions={pgyLevelOptions}
                maxPGYLevel={maxPGYLevel}
                programMaxPGYById={isInstitutionalAdmin ? programMaxPGYById : undefined}
                isInstitutionalAdmin={isInstitutionalAdmin}
                programOptions={programOptions}
                helperText="Selected users will receive an invitation to event"
              />
            </div>
          </div>

          <Form.Group className="mb-3 mt-3">
            <FloatingLabel controlId="floatingInput" label="Description" className="mb-3">
            <Form.Control
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Enter event description"
            />
            </FloatingLabel>
          </Form.Group>

          {/* Attachments Section */}
          <Form.Group className="mb-3">
            <Form.Label>Attachments</Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={handleFileUpload}
              disabled={uploading}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp"
            />
            <Form.Text className="text-muted">
              Supported formats: PDF, Word, Excel, PowerPoint, Text, Images (max 10MB each)
            </Form.Text>
            {uploading && <div className="text-primary mt-1">Uploading...</div>}
          </Form.Group>

          {attachments.length > 0 && (
            <div className="mb-3">
              <div className="border rounded p-2">
                {attachments.map((file, index) => (
                  <div key={index} className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-truncate">{file.name}</span>
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-muted">{formatFileSize(file.size)}</small>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveAttachment(index)}
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          
          {selectedUsers.length > 0 && (
            <div className="mb-3">
              <Form.Label>Invited Users ({selectedUsers.length}):</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {selectedUsers.slice(0, 10).map(userId => {
                  const user = availableUsers.find(u => u.id === userId);
                  if (!user) return null;
                  return (
                    <span key={userId} className="badge rounded bg-secondary d-flex align-items-center gap-2 p-2" style={{ fontSize: '1em' }}>
                      <span className="fw-normal">{user.firstName} {user.lastName}</span>
                      {/*<span className="small">({user.role})</span>*/}
                      <Button
                        variant="link-dng"
                        size="sm"
                        className="px-1 py-0"
                        onClick={() => handleUserSelection(userId)}
                        title="Remove user"
                      >
                        <i className="bi bi-x"></i>
                      </Button>
                    </span>
                  );
                })}
              </div>
              {selectedUsers.length > 10 && (
                <div>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none mt-2"
                    onClick={() => setShowUserDetails(!showUserDetails)}
                  >
                    {showUserDetails ? 'Hide' : `Show ${selectedUsers.length - 10} more users`}                    
                    <i className={`bi bi-chevron-${showUserDetails ? 'up' : 'down'} me-1`}></i>

                  </Button>
                  {showUserDetails && (
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {selectedUsers.slice(10).map(userId => {
                        const user = availableUsers.find(u => u.id === userId);
                        if (!user) return null;
                        return (
                          <span key={userId} className="badge rounded bg-secondary d-flex align-items-center gap-2 p-2" style={{ fontSize: '1em' }}>
                            <span className="fw-normal">{user.firstName} {user.lastName}</span>
                            {/*<span className="small">({user.role})</span>*/}
                            <Button
                              variant="link-dng"
                              size="sm"
                              className="px-1 py-0"
                              onClick={() => handleUserSelection(userId)}
                              title="Remove user"
                            >
                              <i className="bi bi-x"></i>
                            </Button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" size="sm" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={loading}>
              {loading ? "Creating..." : "Save Event"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddEventModal;
